import { NotificationStatus, type Notification, type NotificationStats } from '../types/notification.types';

// GH#2674 — el API serializa la entidad tal cual: guarda `isRead: boolean` y NO manda `status`
// (apps/api/src/core/notifications/entities/notification.entity.ts). El front decide «sin leer» con
// `status` (NotificationItem), así que, sin esta traducción, ninguna notificación del servidor se
// veía sin leer: sin estilo, sin el botón «Marcar como leído» y con «Marcar como no leído» en su lugar.
// Se deriva aquí, en el borde de datos, para no tocar el API ni el tipo. Si algún día el API manda
// `status`, gana ese.
export type NotificacionDelServidor = Omit<Notification, 'status'> & {
  status?: NotificationStatus;
  isRead?: boolean;
};

export function normalizarNotificacion(cruda: NotificacionDelServidor): Notification {
  if (!cruda || typeof cruda !== 'object') return cruda as unknown as Notification;
  return {
    ...cruda,
    status: cruda.status ?? (cruda.isRead === true ? NotificationStatus.READ : NotificationStatus.UNREAD),
  };
}

// GH#2679 — `GET /notifications/stats` llega envuelto, `{ success, data: { total, unread, byType }, … }`, y `privateApi` no
// desenvuelve (el mismo caso de GH#1647 en `marketing-api.ts`). El cliente devolvía el sobre entero, así que `stats.unread` era
// `undefined`: la campana no pintaba el contador ni «Marcar todas como leídas» (ambos dependen de `stats.unread > 0`).
// Esta función hace lo mismo que `desenvolverSobre` de marketing-api; no se importa de allí para no meter ese módulo en el chunk
// del layout (la campana vive en el Header).
const desenvolver = (cuerpo: unknown): unknown =>
  cuerpo !== null && typeof cuerpo === 'object' && !Array.isArray(cuerpo) && 'success' in cuerpo && 'data' in cuerpo
    ? (cuerpo as { data: unknown }).data
    : cuerpo;

const numero = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/** El API manda solo `{ total, unread, byType }`; el tipo del front pide además `read`, `archived` y `byStatus`: se derivan. */
export function normalizarStats(cruda: unknown): NotificationStats {
  const d = (desenvolver(cruda) ?? {}) as Partial<NotificationStats>;
  const total = numero(d.total);
  const unread = numero(d.unread);
  const read = d.read === undefined ? Math.max(0, total - unread) : numero(d.read);
  const archived = numero(d.archived);
  return {
    total,
    unread,
    read,
    archived,
    byType: d.byType ?? ({} as NotificationStats['byType']),
    byStatus: d.byStatus ?? {
      [NotificationStatus.UNREAD]: unread,
      [NotificationStatus.READ]: read,
      [NotificationStatus.ARCHIVED]: archived,
    },
  };
}
